import type { ConfigFairUse, PlanEntitlements } from "@/api/fair-use"

import { SettingsService } from "@/services/settings-service"

/**
 * What the app assumes before `/api/config` has answered, and on any backend
 * that does not send the block at all: fair use OFF, no plan, no row.
 *
 * "Off" is the only safe default in both directions. It enforces nothing
 * locally — the server is the enforcement point either way — so a slow config
 * fetch never flashes a lock on a feature the user actually has, and never
 * silently unlocks one they do not (the WS/HTTP refusal still arrives).
 */
const FAIR_USE_DISABLED: ConfigFairUse = { enabled: false, plan: null, entitlements: null }

/**
 * The signed-in user's `/api/config` fair-use block (fair use round 2, #3087).
 *
 * Read from the settings singleton rather than through `useConfigQuery`, for
 * the same reason the neighbouring route-override hooks do: this value is
 * consulted from the composer and the Explorer toolbar, which both render in
 * standalone harnesses with no `QueryClientProvider`, and `useQuery` throws
 * outright when no client is in context. The singleton is filled once at
 * bootstrap from the very same `/api/config` response, so there is nothing to
 * subscribe to and nothing extra to fetch.
 *
 * Always a value, never undefined, so callers branch on `enabled`/
 * `entitlements` rather than on loading state.
 */
export function useFairUsePlanConfig(): ConfigFairUse {
  let settings: { fair_use?: ConfigFairUse } | undefined
  try {
    settings = SettingsService.getInstance().settings
  } catch {
    // The singleton is created during bootstrap and `getInstance` throws until
    // it exists. A surface that renders that early simply has no plan yet.
    return FAIR_USE_DISABLED
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return settings?.fair_use ?? FAIR_USE_DISABLED
}

/**
 * The 12-field entitlement row for this user, or null when fair use is off,
 * unwired, or the config has not loaded yet.
 *
 * Null is deliberately indistinguishable from "fair use off": a surface that
 * cannot see a row must behave exactly as it did before round 2 (invariant:
 * "fair use disabled ⇒ no entitlement is enforced anywhere").
 */
export function useFairUseEntitlements(): PlanEntitlements | null {
  const fairUse = useFairUsePlanConfig()
  if (!fairUse.enabled) return null
  return fairUse.entitlements
}

/**
 * True when fair use is on, the row has loaded, and it does NOT grant
 * `field` — i.e. the feature is locked by the user's plan rather than by an
 * admin toggle or a route override.
 *
 * Returns false whenever the answer is not yet known, which keeps a locked UI
 * from flashing during bootstrap.
 */
export function useIsPlanFeatureLocked(field: "deep" | "voice"): boolean {
  const entitlements = useFairUseEntitlements()
  if (!entitlements) return false
  return entitlements[field] === false
}
